import { useMemo, useState } from "react";
import { Collapse } from "react-collapse";

import useMeetingStore from "@store/useMeetingStore";
import { useSuggestedTags } from "./hooks/useSuggestedTags";
import { useOptimisticTags } from "./hooks/useOptimisticTags";
import { RelationObjectType } from "@enums/relations.enum";
import { cn } from "@utils/tailwind.util";
import { mergeTagsWithSuggestedTags } from "@utils/tags.util";
import { CollapsibleSectionHeader } from "@components/Headers/CollapsibleSectionHeader";
import { TaggingComponentWithTags } from "../../TaggingComponent/TaggingComponentWithTags";
import { SuggestedTags } from "./SuggestedTags";

// Tag state is owned by the screen (single useOptimisticTags instance shared with NewEntityButtons).
type OptimisticTagsBundle = Pick<
  ReturnType<typeof useOptimisticTags>,
  "currentTags" | "updateTags" | "onRemoveTag" | "temporaryTags"
>;

interface Props extends OptimisticTagsBundle {
  contentType: RelationObjectType;
  className?: string;
  isUpdating?: boolean;
}

export function MeetingTagsSection({
  contentType,
  className,
  isUpdating,
  currentTags,
  updateTags,
  onRemoveTag,
  temporaryTags,
}: Props) {
  const { meetingId } = useMeetingStore();
  const { data: suggestedTags } = useSuggestedTags(contentType, meetingId);

  const [isSectionOpen, setIsSectionOpen] = useState(true);
  const [isDropdownOpened, setIsDropdownOpened] = useState(false);

  const mergedTags = useMemo(
    () => mergeTagsWithSuggestedTags(currentTags, suggestedTags?.companies),
    [currentTags, suggestedTags?.companies]
  );

  const handleToggleSection = () => setIsSectionOpen((prevState) => !prevState);

  const handleOpenDropdown = () => {
    if (!isSectionOpen) setIsSectionOpen(true);
    setIsDropdownOpened(true);
  };

  const handleCloseDropdown = () => setIsDropdownOpened(false);

  const tagsCount = mergedTags?.length || 0;
  const excludedIds = mergedTags.map((item) => item.object.id);

  return (
    <section className={cn("flex flex-col gap-3", className)}>
      <CollapsibleSectionHeader
        title="Tags"
        tagsCount={tagsCount}
        isOpen={isSectionOpen}
        onToggleSection={handleToggleSection}
        onOpenDropdown={handleOpenDropdown}
        isUpdating={isUpdating}
      />

      <Collapse isOpened={isSectionOpen}>
        <TaggingComponentWithTags
          contentType={contentType}
          keyForTypes="MEETING"
          onRemoveTag={onRemoveTag}
          onChangeTags={updateTags}
          selectedTags={mergedTags}
          temporaryTags={temporaryTags}
          excludedIds={excludedIds}
          isDropdownOpened={isDropdownOpened}
          onCloseDropdown={handleCloseDropdown}
          dropdownClassNames="min-w-[280px]"
        />

        <SuggestedTags
          title={"You might also want to tag this meeting to:"}
          suggestedTaggingOptions={{ ...suggestedTags, companies: [] }}
          onAddTags={updateTags}
        />
      </Collapse>
    </section>
  );
}
